import React from 'react';
import { motion } from "framer-motion";
import Layout from '@/components/Layout';
import { Card, CardContent } from "@/components/ui/card";
import TeamSection from '@/components/TeamSection';
import { FaGlobeAmericas, FaShieldAlt, FaUsers, FaChartLine, FaLightbulb, FaHandshake } from 'react-icons/fa';

const AboutUs = () => {
  return (
    <Layout>
      <div className="space-y-16 py-8">
        {/* Hero Section */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About <span className="text-truth">Truth Nexus</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We're on a mission to combat misinformation and empower people with tools to 
              identify, report, and prevent the spread of false information across the digital landscape.
            </p>
          </motion.div>
        </section>
        
        {/* Our Story */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="rounded-lg overflow-hidden">
              <div className="bg-black/30 backdrop-blur-sm border border-gray-800 p-6 rounded-lg">
                <h2 className="text-3xl font-bold mb-4 text-truth">Our Story</h2>
                <p className="mb-4">
                  Truth Nexus was born out of a growing concern for the rapid spread of misinformation 
                  and its real-world consequences especialy In the north west and south west regions of Cameroon that is undergoing a crisis situation. In an era where information travels faster than ever before,
                  the distinction between fact and fiction has become increasingly blurred.
                </p>
                <p className="mb-4">
                  Founded in 2025, our team of technologists, journalists, and researchers came together with a 
                  shared vision: to create advanced AI-powered tools that could help combat the spread of 
                  misinformation while educating the public on media literacy.
                </p>
                <p>
                  Truth Nexus is a startup that stands at the forefront of the battle against digital misinformation, 
                  providing cutting-edge solutions for individuals, organizations, and governments in cameroon and  worldwide.
                </p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-2 gap-4">
              <ValueCard
                icon={<FaShieldAlt />}
                title="Truth"
                description="We are committed to upholding factual accuracy and integrity in all digital content."
              />
              <ValueCard
                icon={<FaGlobeAmericas />}
                title="Global Impact"
                description="Our tools and resources is designed to serve communities worldwide to build information resilience."
              />
              <ValueCard
                icon={<FaUsers />}
                title="Community"
                description="We are committed to fostering a collaborative approach to combating misinformation through education."
              />
              <ValueCard
                icon={<FaLightbulb />}
                title="Innovation"
                description="We leverage cutting-edge AI technology to stay ahead of evolving misinformation tactics."
              />
            </div>
          </motion.div>
        </section>
        
        {/* Founder Section */}
        <section className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-2xl p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-block px-3 py-1 mb-4 border border-truth/30 rounded-full bg-truth/10 text-truth text-sm">
                Our Visionary Leader
              </div>
              <h2 className="text-3xl font-bold mb-4">Wobyeb Graphlain</h2>
              <p className="text-gray-300 mb-6">
                Born and raised in Cameroon north west region, Wobyeb Graphlain is the visionary founder and CEO of Truth Nexus. With a background in both computer science and community development, Wobyeb identified the critical need for technological solutions to address the growing misinformation crisis.
              </p>
              <p className="text-gray-300 mb-6">
                Having witnessed firsthand how misinformation affects communities in Cameroon and across Africa, Wobyeb was determined to create a platform that could serve not just developed nations, but also regions where media literacy and fact-checking resources are scarce.
              </p>
              <p className="text-gray-300 mb-6">
                "In today's world, the truth is our most valuable and yet most vulnerable asset. Truth Nexus exists to protect that asset by putting powerful AI tools in the hands of everyday people," says Graphlain.
              </p>
              <p className="text-gray-300">
                Under his leadership, Truth Nexus is growing from a small startup to a globally recognized force in the fight against misinformation, with innovative solutions that leverage cutting-edge AI technology.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative w-64 h-64">
                <div className="absolute inset-0 bg-truth/30 rounded-full blur-xl"></div>
                <div className="absolute inset-0 rounded-full overflow-hidden border-2 border-truth">
                  {/* Placeholder founder image */}
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                    <span className="text-6xl text-truth">WG</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Investment Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 p-6 bg-black/30 border border-gray-800 rounded-lg"
          >
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl font-bold text-truth">Investment Opportunity</h3>
                <p className="text-gray-300 mt-2">
                  Truth Nexus is currently seeking strategic investors to help scale our revolutionary AI technologies 
                  and expand our global impact in the fight against misinformation.
                </p>
              </div>
              <div className="flex gap-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-truth hover:bg-truth-dark text-white px-4 py-2 rounded-md flex items-center gap-2 cursor-pointer"
                >
                  <FaHandshake />
                  <span>Investor Relations</span>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-black/50 border border-truth/50 text-truth hover:bg-truth/10 px-4 py-2 rounded-md flex items-center gap-2 cursor-pointer"
                >
                  <FaChartLine />
                  <span>View Pitch Deck</span>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </section>
        
        {/* Team Section */}
        <TeamSection />

      </div>
    </Layout>
  );
};

const ValueCard = ({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) => (
  <Card className="bg-black/30 border-gray-800 hover:border-truth/30 transition-all">
    <CardContent className="p-6">
      <div className="text-truth text-2xl mb-3">{icon}</div>
      <h3 className="font-bold mb-2">{title}</h3>
      <p className="text-sm text-gray-400">{description}</p>
    </CardContent>
  </Card>
);

export default AboutUs;
